/**
 * Tile sources, behind one swappable interface (CLAUDE.md, World data).
 *
 * Everything the streamer knows about a data source is on {@link TileProvider}: where a tile
 * lives, how deep it goes, and how to credit it. Swapping the imagery for a keyed Mapbox or
 * MapTiler layer later, or routing through a proxy, is a new implementation of this interface
 * and nothing else — which is the point, because the free Sentinel-2 layer is non-commercial
 * and will eventually have to be replaced.
 *
 * The two shipped sources need no API key, which is non-negotiable #10.
 *
 * `updateFixed` / `updateFrame`: neither. Providers are pure address books.
 */

/** What a provider serves. */
export type TileProviderKind = 'elevation' | 'imagery';

export interface TileProvider {
  /** Stable identifier; used in cache keys, so changing it invalidates cached tiles. */
  readonly id: string;
  readonly kind: TileProviderKind;
  /** Shallowest zoom served. */
  readonly minZoom: number;
  /** Deepest zoom served. Requests past it are clamped, not failed. */
  readonly maxZoom: number;
  /** Edge length of a served tile [px]. */
  readonly tileSizePx: number;
  /** Credit line, reproduced verbatim in the README and the credits screen. */
  readonly attribution: string;
  /** Absolute URL of one tile. */
  url(zoom: number, x: number, y: number): string;
}

/**
 * AWS Terrain Tiles (Mapzen), terrarium encoding — the elevation source.
 *
 * Heights are packed into an ordinary PNG: `height_m = R·256 + G + B/256 − 32768`. That gives
 * 1/256 m resolution over a −32768…+32767 m range, which is far more than the underlying data
 * (SRTM at ~30 m posts, GMTED and ETOPO1 where SRTM does not reach) actually resolves. Ocean
 * reads as a flat 0 or slightly below, which is exactly what the water mask wants.
 *
 * Public, keyless, and on the AWS Open Data programme. Coverage runs to z15 over most land;
 * past z14 the data is mostly resampled rather than new detail, so
 * {@link TerrariumElevationProvider.maxZoom} is set where the information actually stops.
 */
export class TerrariumElevationProvider implements TileProvider {
  readonly id = 'aws-terrarium';
  readonly kind = 'elevation' as const;
  readonly minZoom = 0;
  readonly maxZoom: number;
  readonly tileSizePx = 256;
  readonly attribution =
    'Elevation: Terrain Tiles by Mapzen/AWS Open Data (SRTM/NASA, GMTED, ETOPO1, and others)';

  private readonly baseUrl: string;

  constructor(options: { maxZoom?: number; baseUrl?: string } = {}) {
    /*
     * 15, and it was 14 on a guess that measurement contradicts.
     *
     * The old comment reasoned that "past z14 the data is mostly resampled rather than new
     * detail", which is true of the *elevation*, and concluded the cap belonged at 14. But this
     * number does not only cap elevation — `terrainSystem.ts` takes `min(elevation.maxZoom,
     * imagery.maxZoom)` for the whole quadtree, so it was capping the **imagery** too, and
     * imagery has plenty of new detail past z14 once the source is not a 10 m/px satellite.
     *
     * Measured against the live service: z14 and z15 both return 200 with real PNGs (70 KB and
     * 57 KB over San Francisco); **z16 and z17 return 404.** So 15 is where the source actually
     * stops, and it is worth a zoom of imagery sharpness plus genuinely finer relief.
     */
    this.maxZoom = options.maxZoom ?? 15;
    this.baseUrl = options.baseUrl ?? 'https://s3.amazonaws.com/elevation-tiles-prod/terrarium';
  }

  url(zoom: number, x: number, y: number): string {
    return `${this.baseUrl}/${zoom}/${x}/${y}.png`;
  }
}

/**
 * Decode one terrarium pixel to metres above the ellipsoid.
 *
 * Exported and tested separately because getting the `/256` term wrong produces terrain that is
 * *almost* right — correct mountains, subtly wrong valleys — which is very hard to spot by eye.
 */
export function decodeTerrariumHeightM(r: number, g: number, b: number): number {
  return r * 256 + g + b / 256 - 32768;
}

/**
 * EOX Sentinel-2 cloudless — the imagery source.
 *
 * Note the axis order: this is a WMTS endpoint, and its path is `{z}/{y}/{x}`, **not** the
 * `{z}/{x}/{y}` of an XYZ service. Getting it backwards yields a plausible-looking mosaic of
 * the wrong parts of the planet, so the swap happens here, once, rather than at call sites.
 *
 * The layer is published per year; `2024` is current. Non-commercial licence, which is fine
 * while the project is free and is the reason this sits behind {@link TileProvider}.
 */
export class EoxImageryProvider implements TileProvider {
  readonly id: string;
  readonly kind = 'imagery' as const;
  readonly minZoom = 0;
  readonly maxZoom: number;
  readonly tileSizePx = 256;
  readonly attribution =
    'Imagery: Sentinel-2 cloudless — s2maps.eu by EOX IT Services GmbH (contains modified ' +
    'Copernicus Sentinel data)';

  private readonly baseUrl: string;

  constructor(options: { year?: number; maxZoom?: number; baseUrl?: string } = {}) {
    const year = options.year ?? 2024;
    this.id = `eox-s2cloudless-${year}`;
    // Sentinel-2 is a 10 m/px instrument, and z14 is 9.55 m/px at the equator (7.6 m/px at San
    // Francisco) — so z14 is exactly where the real information stops and the service starts
    // upsampling. Detail textures take over from there; see terrainMaterial.ts.
    //
    // This comment used to say z14 was "~2.4 m/px at the equator". That is z16. The conclusion
    // was right and the number was wrong by 4x, which matters because it makes the imagery look
    // four times sharper than it is when you are reasoning about whether the ground should be
    // blocky at a given altitude. It should: at 4700 ft AGL the display resolves ~1.4 m/px and
    // the imagery supplies 7.6, so it is 5x coarser than the screen and no amount of streaming
    // changes that. The near-field detail layer is the only lever.
    /*
     * 17, though the *information* still stops at 14.
     *
     * This is the global fallback: wherever NAIP does not answer, this has to serve whatever zoom
     * the quadtree selected, or the tile renders untextured. So this number is not a claim about
     * resolution — it is the promise the quadtree's depth is built on, and it has to hold
     * everywhere, not just where the good imagery is. Verified against the live service at z17
     * over Innsbruck, Tokyo and Cairo as well as San Francisco: 200, `image/jpeg`, 6–7 KB.
     *
     * Past z14 it is magnifying 10 m pixels rather than revealing anything, which is honest and
     * is still far better than a grey hole. It was 16, which was low for no reason beyond that
     * being as deep as anything had asked to go.
     */
    this.maxZoom = options.maxZoom ?? 17;
    this.baseUrl =
      options.baseUrl ??
      `https://tiles.maps.eox.at/wmts/1.0.0/s2cloudless-${year}_3857/default/g`;
  }

  url(zoom: number, x: number, y: number): string {
    return `${this.baseUrl}/${zoom}/${y}/${x}.jpg`;
  }
}

/**
 * NAIP — 1 m aerial imagery over the United States, public domain, no key.
 *
 * ## Why this exists
 *
 * A pilot reported the ground as "soooo bad" at 4,000 ft, and the arithmetic agreed: Sentinel-2 is
 * a **10 m/px instrument**, so at San Francisco's latitude z14 is 7.57 m/px against a display that
 * resolves about 1.4 — five times coarser than the screen, and z14 is only 1:1 above 25,000 ft.
 * That is the sensor, not the streamer, and no amount of zoom invents pixels that were never
 * recorded.
 *
 * The National Agriculture Imagery Program flies the continental United States at **1 m** (0.6 m in
 * places) on a two-to-three year cycle. It is a work of the US federal government and therefore
 * **public domain**; the service sends no copyright text and asks for no key.
 *
 * ## What was verified before trusting it, because a URL that resolves is not a URL that works
 *
 * Against the live service, from a browser origin:
 *
 *   - `.../ImageServer?f=json` reports `pixelSizeX/Y: 1`, `spatialReference: 3857` and a
 *     `tileInfo` with 256×256 tiles, 18 levels and the standard Web Mercator origin — i.e. it is
 *     an ordinary XYZ cache in the projection the streamer already speaks.
 *   - A tile over KSFO returns **HTTP 200, `image/jpeg`, 256×256**, at z16 *and* z17.
 *   - **`access-control-allow-origin` reflects the requesting origin.** Without that none of this
 *     would work from a browser and the whole idea would need a proxy, which would need a backend.
 *
 * ## Coverage stops, and it stops hard
 *
 * CONUS land only. Innsbruck returns 404. **Open water returns 404** — which matters a great deal
 * for a simulator whose flagship airport sits on a bay, because a 404 here means an untextured
 * tile. That is why {@link TerrainSystem} carries a *fallback* provider and the worker retries:
 * NAIP where it answers, Sentinel-2 everywhere else, decided per tile by the service rather than
 * by a bounding box this file would have to keep correct.
 */
export class NaipImageryProvider implements TileProvider {
  readonly id = 'usda-naip-conus';
  readonly kind = 'imagery' as const;
  readonly minZoom = 0;
  readonly maxZoom: number;
  readonly tileSizePx = 256;
  readonly attribution = 'Imagery (United States): NAIP by USDA Farm Service Agency (public domain)';

  private readonly baseUrl: string;

  constructor(options: { maxZoom?: number; baseUrl?: string } = {}) {
    // The service publishes 18 levels, z0…z17, and z17 is 0.95 m/px at San Francisco — about the
    // native resolution of the imagery, so there is nothing past it worth asking for.
    this.maxZoom = options.maxZoom ?? 17;
    this.baseUrl =
      options.baseUrl ??
      'https://gis.apfo.usda.gov/arcgis/rest/services/NAIP/USDA_CONUS_PRIME/ImageServer/tile';
  }

  /** ArcGIS orders its tile path `level/row/col`, which is `{z}/{y}/{x}` — as EOX's WMTS does. */
  url(zoom: number, x: number, y: number): string {
    return `${this.baseUrl}/${zoom}/${y}/${x}`;
  }
}

/**
 * A provider that serves nothing.
 *
 * Not a curiosity: it is what the terrain system falls back to when a source is unreachable, so
 * an offline dev session or a blocked domain degrades to untextured relief rather than an error
 * overlay. It is also what the tests use, so they never touch the network.
 */
export class NullTileProvider implements TileProvider {
  readonly id: string;
  readonly kind: TileProviderKind;
  readonly minZoom = 0;
  readonly maxZoom = 0;
  readonly tileSizePx = 256;
  readonly attribution = '';

  constructor(kind: TileProviderKind) {
    this.kind = kind;
    this.id = `null-${kind}`;
  }

  url(): string {
    return '';
  }
}

/** Every attribution the given providers require, deduplicated and in a stable order. */
export function collectAttributions(providers: readonly TileProvider[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const provider of providers) {
    if (provider.attribution === '' || seen.has(provider.attribution)) continue;
    seen.add(provider.attribution);
    out.push(provider.attribution);
  }
  return out;
}
